import { ArrowPathIcon, ExclamationTriangleIcon, SparklesIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useBlocker, useNavigate } from "react-router-dom";

import { Button, Dialog, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import { getPromptTestRun, startPromptTestRun } from "@api/rest/prompt-test-bed";
import type {
  ComparisonField,
  ComparisonRun,
  ComparisonScoreSummary,
  EvalCheckResult,
  EvalModelComparisonDetail,
  EvalMultiModelTestDetail,
  MultiModelTestReviewItem,
} from "@api/rest/types-and-hooks";
import {
  getEvalModelComparison,
  getEvalMultiModelTest,
  getGetEvalMultiModelTestQueryKey,
  getListEvalModelComparisonsQueryKey,
  markComparisonPromptSeen,
  updateEvalModelComparison,
} from "@api/rest/types-and-hooks";
import { RuleCheckList } from "@components/EvalReview/RuleCheckList";
import {
  CHECK_STYLES,
  checkStatus,
  isCheckWorthShowing,
  isDisqualifyingFailure,
  metricLabel,
} from "@components/EvalReview/constants";
import { GeneratingSkeleton } from "@components/LlmOutput/GeneratingSkeleton";
import { LlmOutputView } from "@components/LlmOutput/LlmOutputView";
import { ConfettiBurst } from "@components/ModelTesting/ConfettiBurst";
import { PairwiseDifferences, PairwiseVerdictChip } from "@components/ModelTesting/PairwiseVerdict";
import { RunLlmInput } from "@components/ModelTesting/RunLlmInput";
import {
  type DraftPicks,
  type FeedbackBaseline,
  anyRunning,
  armState,
  awaitingJudge,
  baselineFromComparison,
  comparisonProgress,
  draftPicksFromFields,
  hasUnsavedFeedback,
  outputsCompared,
  picksForPatch,
  setPickNote,
  togglePick,
  unreviewedQueueItems,
  withSavedPicks,
} from "@components/ModelTesting/comparisonState";
import {
  Arm,
  ArmState,
  type ArmValue,
  OutputMatch,
  PageLocation,
  PairwiseStatus,
  RunStatus,
  VERDICT_LABELS,
  Verdict,
  type VerdictValue,
  Winner,
  type WinnerValue,
  modelLabel,
  scoreTone,
} from "@components/ModelTesting/constants";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { ReportType } from "@pages/Templates/constants";

const comparisonQueryKey = (id: number) => ["eval-model-comparison", id];

// Auto-start tries a conclusions slot twice (one retry for a transient
// failure); beyond that the manual Start run button is the recovery.
const MAX_CONCLUSIONS_START_ATTEMPTS = 2;
const MAX_REMAINING_SHOWN = 5;
// Stable empty value so an unhighlighted cell's scroll effect doesn't refire.
const NO_QUOTES: string[] = [];
// The app header is h-14; the gap keeps a focused field's title off the bar.
const APP_HEADER_PX = 56;
const FOCUS_GAP_PX = 16;

/** Checks compressed for a side-by-side read, above the output so they sit
 *  with the judge's verdict: failures lead with reasons, passes collapse to
 *  a count; the full table reuses the eval page's RuleCheckList. */
const ChecksSummary: FC<{ checks: EvalCheckResult[]; onHighlight: (quotes: string[]) => void }> = ({
  checks: allChecks,
  onHighlight,
}) => {
  const [expanded, setExpanded] = useState(false);
  const checks = allChecks.filter(isCheckWorthShowing);
  if (checks.length === 0) return null;

  // A 1/5 judge score is the rubric's bottom anchor and disqualifies
  // outright, so it leads.
  const disqualifying = checks.filter(isDisqualifyingFailure);
  const failed = checks.filter((c) => c.passed === false && !isDisqualifyingFailure(c));
  const passed = checks.filter((c) => c.passed === true);
  const info = checks.filter((c) => c.passed === null);
  const orderedFailures = [...disqualifying, ...failed];

  return (
    <div className="mb-4 border-b border-border-medium pb-4">
      {disqualifying.length > 0 ? (
        <div className="mb-2 flex items-start gap-2 rounded-md bg-red-600 px-2.5 py-1.5 text-white">
          <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span className="text-xs font-semibold">
            Disqualifying — scored 1 of 5 on {disqualifying.map((c) => metricLabel(c.metric_name)).join(", ")}.
          </span>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {disqualifying.map((c) => (
          <span key={c.metric_name} className="rounded bg-red-600 px-1.5 py-0.5 text-xs font-semibold text-white">
            {metricLabel(c.metric_name)}
          </span>
        ))}
        {failed.map((c) => (
          <span
            key={c.metric_name}
            className={`rounded px-1.5 py-0.5 text-xs font-medium ${CHECK_STYLES[checkStatus(false)]}`}
          >
            {metricLabel(c.metric_name)}
          </span>
        ))}
        {passed.length > 0 ? (
          <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${CHECK_STYLES[checkStatus(true)]}`}>
            {passed.length} passed
          </span>
        ) : null}
        {info.length > 0 ? <span className="text-xs text-text-tertiary">{info.length} informational</span> : null}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="ml-auto rounded-md border border-border-medium bg-bg-surface px-2 py-0.5 text-xs font-medium text-text-secondary shadow-sm transition-colors hover:bg-bg-default"
        >
          {expanded ? "Hide checks" : "All checks"}
        </button>
      </div>
      {!expanded && orderedFailures.length > 0 ? (
        /* One bg-ai-subtle block per check, sparkle carrying the AI attribution. */
        <div className="mt-2 flex flex-col gap-1.5">
          {orderedFailures.map((c) => (
            <div key={c.metric_name} className="flex items-start gap-x-2 rounded-lg bg-ai-subtle p-3">
              <SparklesIcon className="mt-0.5 size-4 shrink-0 text-ai" aria-hidden="true" />
              <p className="text-sm text-text-primary">
                <span className="sr-only">AI reviewer — </span>
                <span className="font-medium">{metricLabel(c.metric_name)}:</span> {c.reason}
              </p>
            </div>
          ))}
        </div>
      ) : null}
      {expanded ? <RuleCheckList checks={checks} onHighlight={onHighlight} /> : null}
    </div>
  );
};

/** A model as a badge — human-readable name, raw id on hover. */
const ModelBadge: FC<{ model: string }> = ({ model }) => (
  <span
    title={model}
    className="rounded-md border border-border-medium bg-bg-surface px-1.5 py-0.5 text-xs font-medium text-text-primary"
  >
    {modelLabel(model)}
  </span>
);

/** Overall notes, verdict buttons, and Save — one row, host-agnostic
 *  (fixed footer on standalone comparisons, inline card in queue mode). */
const FeedbackControls: FC<{
  notes: string;
  onNotes: (value: string) => void;
  dirty: boolean;
  verdict: VerdictValue;
  onVerdict: (value: VerdictValue) => void;
  saving: boolean;
  onSave: () => void;
}> = ({ notes, onNotes, dirty, verdict, onVerdict, saving, onSave }) => (
  <div className="flex items-center gap-x-4">
    <input
      type="text"
      value={notes}
      onChange={(e) => onNotes(e.target.value)}
      placeholder="Overall notes — what would you want the person flipping the default to know?"
      className="min-w-0 flex-1 rounded border border-border-subtle bg-bg-default px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-limestone-900"
    />
    {dirty ? (
      <span className="shrink-0 text-xs font-semibold text-amber-700" role="status">
        Unsaved changes
      </span>
    ) : null}
    <div className="flex shrink-0 items-center gap-1">
      {(Object.values(Verdict) as VerdictValue[]).map((value) => (
        <button key={value} type="button" className={pickClasses(verdict === value)} onClick={() => onVerdict(value)}>
          {VERDICT_LABELS[value]}
        </button>
      ))}
    </div>
    <Button
      text={saving ? "Saving…" : "Save"}
      appearance="primary"
      className="shrink-0 rounded-sm"
      disabled={saving}
      onClick={onSave}
    />
  </div>
);

const runIsDisqualified = (run: ComparisonRun | null): boolean =>
  (run?.check_results ?? []).some(isDisqualifyingFailure);

/** One arm of one field: the output, or the state that stands in for it. */
const ArmCell: FC<{
  promptKey: string;
  run: ComparisonRun | null;
  onRestart: (() => void) | null;
  restarting: boolean;
  /** The other arm's output — enables the candidate's Diff view. */
  diffAgainst?: string | null;
  /** A check's quotes to mark in the output ("show N in the output"). */
  highlights: string[];
  onClearHighlights: () => void;
}> = ({ promptKey, run, onRestart, restarting, diffAgainst, highlights, onClearHighlights }) => {
  const outputRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (highlights.length > 0) outputRef.current?.scrollIntoView({ block: "nearest" });
  }, [highlights]);
  const state = armState(run);

  if (state === ArmState.Running) {
    return <GeneratingSkeleton />;
  }

  if (state === ArmState.Pending || state === ArmState.Failed) {
    return (
      <div className="flex flex-col items-start gap-2">
        <span className="flex items-center gap-1.5 text-sm text-text-error-primary">
          <XCircleIcon className="size-3.5" />
          {state === ArmState.Failed ? "Run failed." : "Run never started."}
        </span>
        {run ? <RunLlmInput promptKey={promptKey} runId={run.run_id} /> : null}
        {onRestart ? (
          <Button
            text={restarting ? "Starting…" : state === ArmState.Failed ? "Run again" : "Start run"}
            appearance="secondary"
            className="rounded-sm"
            disabled={restarting}
            onClick={onRestart}
          />
        ) : (
          <span className="text-xs text-text-tertiary">
            The profile this comparison ran on was deleted, so the run can’t be repeated.
          </span>
        )}
      </div>
    );
  }

  return (
    <>
      <RunLlmInput promptKey={promptKey} runId={run!.run_id} />
      {/* The output is the thing compared, so it sits on its own page. */}
      <div ref={outputRef} className="rounded-md border border-border-medium bg-bg-surface p-4 shadow-sm">
        {highlights.length > 0 ? (
          <div className="mb-3 flex items-start justify-between gap-x-3 rounded border border-globemallow-300 bg-globemallow-50 px-2 py-1">
            {/* The quotes themselves: a check can quote a JSON key that renders
                as a heading, so a count could claim marks that aren't there. */}
            <span className="text-xs text-globemallow-800">
              The check objected to:{" "}
              {highlights.slice(0, 3).map((quote, index) => (
                <span key={`${quote}-${index}`}>
                  {index > 0 ? ", " : ""}
                  <span className="font-medium">
                    &ldquo;{quote.length > 60 ? `${quote.slice(0, 60)}…` : quote}&rdquo;
                  </span>
                </span>
              ))}
              {highlights.length > 3 ? ` and ${highlights.length - 3} more` : ""}
              {". Marked below where it appears (Preview and Raw)."}
            </span>
            <button
              type="button"
              onClick={onClearHighlights}
              className="shrink-0 text-xs font-medium text-globemallow-800 underline"
            >
              Clear
            </button>
          </div>
        ) : null}
        <LlmOutputView result={run!.result} diffAgainst={diffAgainst} highlights={highlights} />
      </div>
    </>
  );
};

/** Before a verdict: identical outputs, a judge still working, or a judge
 *  that failed. Once a verdict lands, the verdict chip carries it. */
const JudgeStateChip: FC<{ field: ComparisonField }> = ({ field }) => {
  const match = outputsCompared(field);
  if (match === null || field.pairwise_verdict != null) return null;
  const chip: [string, string] | null =
    match === OutputMatch.Identical
      ? ["Identical to current", "bg-green-100 text-green-700"]
      : field.pairwise_status === PairwiseStatus.Failed
        ? ["Judge failed — compare by hand", "bg-amber-100 text-amber-700"]
        : awaitingJudge(field)
          ? ["Judging…", "animate-pulse border border-border-medium bg-bg-default text-text-secondary"]
          : null;
  if (chip === null) return null;
  const [label, classes] = chip;
  return (
    <span className={`mb-2 inline-block self-start rounded-md px-2.5 py-1 text-sm font-semibold ${classes}`}>
      {label}
    </span>
  );
};

const fieldAnchor = (promptKey: string) => `comparison-field-${promptKey}`;

/** One quiet stats line: the judge's tally. The per-prompt jump chips only
 *  render outside a review queue — inside one, the queue bar owns
 *  navigation and the chip pile is pure noise. */
const ScoreTotal: FC<{
  summary: ComparisonScoreSummary;
  fields: ComparisonField[];
  showJumpLinks: boolean;
}> = ({ summary, fields, showJumpLinks }) => {
  if (summary.scored === 0 && summary.not_judged === 0) return null;
  const flagged = fields.filter((f) => f.needs_review);
  const parts = [
    summary.worse > 0 ? `${summary.worse} worse` : null,
    summary.unsure > 0 ? `${summary.unsure} not sure` : null,
    summary.not_judged > 0 ? `${summary.not_judged} judging` : null,
  ].filter((part): part is string => part !== null);
  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <span
          className={`rounded-md px-2.5 py-1 text-sm font-semibold ${scoreTone(summary)}`}
          title="Fields the judge called same as or better than the current model"
        >
          {summary.passed} of {summary.total} pass
        </span>
        {parts.length > 0 ? <span className="text-sm text-text-secondary">{parts.join(" · ")}</span> : null}
        {flagged.length > 0 ? (
          <span
            className="rounded-md bg-red-100 px-2.5 py-1 text-sm font-semibold text-red-600"
            title="Judged worse or not sure, a disqualifying check, or not judged yet"
          >
            {flagged.length} to review
          </span>
        ) : null}
      </div>
      {showJumpLinks && flagged.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-text-tertiary">Jump to:</span>
          {flagged.map((f) => (
            <a
              key={f.prompt_key}
              href={`#${fieldAnchor(f.prompt_key)}`}
              className="rounded border border-red-200 bg-bg-surface px-1.5 py-0.5 text-xs text-red-600 hover:bg-red-50"
            >
              {f.field_title || f.prompt_key}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
};

// Neutral on purpose (same anchoring rule as the score grid), but still
// pressable: border, shadow, dark fill when selected.
const pickClasses = (selected: boolean) =>
  `rounded-md border px-3 py-1.5 text-xs font-medium shadow-sm transition-colors ${
    selected
      ? // rio-600 fill: the score grid's "you chose this" treatment.
        "border-rio-600 bg-rio-600 text-white"
      : "border-border-medium bg-bg-surface text-text-primary hover:bg-bg-default"
  }`;

interface Props {
  comparisonId: number;
  /** Called with the owning multi-model test's id when this comparison is a
   *  cell, so the caller can route back to the test results instead of the
   *  landing page. */
  /** candidateModel preselects that model's work-list on the results page. */
  onBack: (testId: number | null, candidateModel?: string) => void;
  /** Scroll this field's card into view once loaded (work-list links). */
  focusPromptKey?: string;
  /** Present when opened from a multi-model test's review work-list — renders the
   *  queue bar (n of m, prev / next-unreviewed) for this candidate. */
  multiTestId?: number;
  /** Lets the tab shell guard its own navigations (the tab switch). */
}

/** Paired outputs judged field by field. Polls while any run is live: a run's
 *  status only moves when its own detail endpoint is read, so each poll
 *  touches every running run before re-reading the comparison. */
const highlightKey = (comparisonId: number, promptKey: string, arm: string): string =>
  `${comparisonId}:${promptKey}:${arm}`;

export const ComparisonScreen: FC<Props> = ({ comparisonId, onBack, focusPromptKey, multiTestId }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Which key the one-shot scroll was applied for — search-param-only
  // navigation (queue next within one comparison) doesn't remount.
  const focusApplied = useRef<string | null>(null);
  const queueBarRef = useRef<HTMLDivElement | null>(null);

  const { data: comparison, isError } = useQuery({
    queryKey: comparisonQueryKey(comparisonId),
    queryFn: async (): Promise<EvalModelComparisonDetail> => {
      const previous = queryClient.getQueryData<EvalModelComparisonDetail>(comparisonQueryKey(comparisonId));
      if (previous) {
        // Serial within a field: sibling-verdict reuse needs the second
        // persist to see the first. Parallel across fields.
        await Promise.all(
          previous.fields.map(async (f) => {
            for (const run of [f.baseline, f.candidate]) {
              if (run?.status === RunStatus.Running) {
                await getPromptTestRun(f.prompt_key, run.run_id).catch(() => null);
              }
            }
          }),
        );
      }
      const resp = await getEvalModelComparison(comparisonId);
      if (resp.status !== 200) throw new Error(`Comparison load failed (${resp.status}).`);
      return resp.data;
    },
    refetchInterval: (query) => (query.state.data && anyRunning(query.state.data.fields) ? 2500 : false),
  });

  // Seeded once per comparison — polling refetches must not clobber typing.
  const [picks, setPicks] = useState<DraftPicks>({});
  const [notes, setNotes] = useState("");
  const [verdict, setVerdict] = useState<VerdictValue>(Verdict.Undecided);
  const [seededId, setSeededId] = useState<number | null>(null);
  // The review queue: this candidate's flagged items across the whole
  // test, in work-list order. Reviewed = a saved pick (winner set).
  const { data: multiTestDetail, isPending: multiTestPending } = useQuery({
    queryKey: getGetEvalMultiModelTestQueryKey(multiTestId ?? 0),
    enabled: multiTestId != null,
    queryFn: async () => {
      const resp = await getEvalMultiModelTest(multiTestId!);
      return resp.status === 200 ? resp.data : null;
    },
  });

  // Jump to the field a work-list link targeted, once it exists in
  // the DOM. One-shot per key — later polls must not yank the scroll.
  useEffect(() => {
    // Keyed on comparison + prompt: queue Next often lands on the SAME
    // prompt key in another template's comparison, which must re-focus
    // and re-stamp seen.
    const focusKey = `${comparisonId}:${focusPromptKey ?? ""}`;
    if (!focusPromptKey || !comparison || focusApplied.current === focusKey) return;
    // In a queue, wait for the test so the sticky bar exists to offset against.
    if (multiTestId != null && multiTestPending) return;
    const el = document.getElementById(fieldAnchor(focusPromptKey));
    if (el) {
      focusApplied.current = focusKey;
      // Instant (a smooth scroll across long outputs drags), and clear of the
      // header plus the sticky queue bar, whose height varies with its rows.
      el.style.scrollMarginTop = `${APP_HEADER_PX + (queueBarRef.current?.offsetHeight ?? 0) + FOCUS_GAP_PX}px`;
      el.scrollIntoView({ block: "start" });
      if (multiTestId != null) {
        // Trail marker: opening an item stamps it "seen" (≠ reviewed —
        // that stays derived from a saved pick). Fire-and-forget.
        void markComparisonPromptSeen(comparisonId, { prompt_key: focusPromptKey })
          .then((resp) => {
            if (resp.status === 200 && resp.data.marked) {
              void queryClient.invalidateQueries({ queryKey: getGetEvalMultiModelTestQueryKey(multiTestId) });
            }
          })
          .catch(() => {
            // Losing a trail marker is harmless; never surface an error for it.
          });
      }
    }
  }, [focusPromptKey, comparison, multiTestId, multiTestPending, comparisonId, queryClient]);

  const queue: MultiModelTestReviewItem[] = useMemo(() => {
    if (!multiTestDetail || !comparison) return [];
    const rollup = multiTestDetail.models.find((m) => m.candidate_model === comparison.candidate_model);
    return rollup?.review ?? [];
  }, [multiTestDetail, comparison]);
  const queueIndex = queue.findIndex(
    (item) => item.comparison_id === comparisonId && (!focusPromptKey || item.prompt_key === focusPromptKey),
  );
  // Queue navigation SAVES then moves — Prev/Next are part of the review
  // flow, so drafts commit (which is also what marks the item reviewed);
  // only actually leaving the flow warns about unsaved feedback.
  const goToQueueItem = async (item: MultiModelTestReviewItem) => {
    if (dirty) {
      const saved = await save();
      if (!saved) return; // stay — the error toast says why
    }
    navigate(
      `/model-testing/comparisons/${item.comparison_id}?multi=${multiTestId}&focus=${encodeURIComponent(item.prompt_key)}`,
    );
  };
  const remaining = useMemo(() => unreviewedQueueItems(queue), [queue]);
  const nextUnreviewed = useMemo(() => {
    if (queue.length === 0) return null;
    for (let step = 1; step <= queue.length; step++) {
      const item = queue[(Math.max(queueIndex, 0) + step) % queue.length];
      if (!item.winner && !(item.comparison_id === comparisonId && item.prompt_key === focusPromptKey)) return item;
    }
    return null;
  }, [queue, queueIndex, comparisonId, focusPromptKey]);

  // Confetti when the LAST verdict lands (transition to all-reviewed
  // while on this page — usually the queue's "Save & finish").
  const [celebrate, setCelebrate] = useState(false);
  // With the confetti, surface the verdict dialog — the inline section
  // sits at the bottom of a long page and is easy to never scroll to.
  const [verdictDialogOpen, setVerdictDialogOpen] = useState(false);
  const prevAllReviewedRef = useRef<boolean | null>(null);
  const allQueueReviewed = queue.length > 0 && queue.every((q) => q.winner);
  useEffect(() => {
    if (queue.length === 0) return;
    if (prevAllReviewedRef.current === false && allQueueReviewed) {
      setCelebrate(true);
      setVerdictDialogOpen(true);
    }
    prevAllReviewedRef.current = allQueueReviewed;
  }, [allQueueReviewed, queue.length]);

  // What the server holds. Updated synchronously on a successful save, so
  // "Saved" and "Unsaved changes" can never be true at once.
  const [baseline, setBaseline] = useState<FeedbackBaseline | null>(null);
  useEffect(() => {
    if (!comparison || seededId === comparisonId) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSeededId(comparisonId);
    const seeded = baselineFromComparison(comparison);
    setBaseline(seeded);
    setPicks(seeded.picks);
    setNotes(seeded.notes);
    setVerdict(seeded.verdict as VerdictValue);
  }, [comparison, comparisonId, seededId]);

  const [restartingSlots, setRestartingSlots] = useState<Set<string>>(new Set());
  // Quotes marked in each output. Keyed by comparison too: queue navigation
  // keeps this screen mounted, and templates share prompt keys.
  const [highlights, setHighlights] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);

  // DIAGNOSIS_EVALUATION: conclusions-page fields start LAST — their
  // prompts consume the other sections' outputs (the server injects the
  // same arm's completed outputs at start). The kickoff skips them; this
  // effect starts each arm's conclusions once that arm's sections are
  // complete. Attempts per slot+arm are capped so a persistent failure
  // can't loop on every 2.5s poll (one retry covers a transient blip);
  // after that the field card's manual Start run is the recovery.
  const conclusionsAttempts = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    if (!comparison || comparison.report_type !== ReportType.DIAGNOSIS_EVALUATION) return;
    if (comparison.learner_test_profile_id == null) return;
    const fields = comparison.fields ?? [];
    const conclusions = fields.filter((f) => f.page_location === PageLocation.Conclusions);
    if (conclusions.length === 0) return;
    const sections = fields.filter((f) => f.page_location !== PageLocation.Conclusions);
    const armModel: Record<ArmValue, string> = {
      [Arm.Baseline]: comparison.baseline_model,
      [Arm.Candidate]: comparison.candidate_model,
    };
    for (const arm of [Arm.Baseline, Arm.Candidate] as ArmValue[]) {
      const armReady = sections.every((f) => f[arm]?.status === RunStatus.Complete);
      if (!armReady) continue;
      for (const field of conclusions) {
        // Keyed by comparison too — a queue hop changes the id without
        // remounting, and templates share prompt keys.
        const attemptKey = `${comparison.id}:${field.prompt_key}:${arm}`;
        const attempts = conclusionsAttempts.current.get(attemptKey) ?? 0;
        if (field[arm] !== null || attempts >= MAX_CONCLUSIONS_START_ATTEMPTS) continue;
        conclusionsAttempts.current.set(attemptKey, attempts + 1);
        void startPromptTestRun(field.prompt_key, {
          learner_test_profile_id: comparison.learner_test_profile_id,
          model: armModel[arm],
          comparison_id: comparison.id,
          arm,
          use_draft: false,
        })
          .then(() => queryClient.invalidateQueries({ queryKey: comparisonQueryKey(comparisonId) }))
          .catch((e: unknown) => {
            // Always report: a retry only happens if the effect re-fires,
            // and polling stops once nothing is running — so a silent
            // first failure could leave the slot pending with no signal.
            // The attempt cap bounds this to one toast per retry.
            notifyError(
              `Could not start ${field.field_title || field.prompt_key} (${arm}): ${
                e instanceof Error ? e.message : "unknown error"
              }`,
            );
          });
      }
    }
  }, [comparison, comparisonId, queryClient]);

  // Unsaved feedback guards every way out: back button, tab shell,
  // router blocker, beforeunload.
  // Not dirty until the drafts have been seeded from the server —
  // otherwise saved feedback reads as unsaved on first paint.
  const dirty = baseline !== null && seededId === comparisonId && hasUnsavedFeedback(baseline, picks, notes, verdict);

  // pendingBack = the ← All comparisons button; otherwise the router blocker.
  const [unsavedWarningOpen, setUnsavedWarningOpen] = useState(false);
  const pendingBack = useRef(false);
  // Navigation within this test's review queue is never blocked:
  // goToQueueItem saves before it navigates, so drafts can't be lost
  // there — the warning is for actually leaving the flow.
  const blocker = useBlocker(
    ({ nextLocation }) =>
      dirty &&
      !(
        multiTestId != null &&
        nextLocation.pathname.startsWith("/model-testing/comparisons/") &&
        new URLSearchParams(nextLocation.search).get("multi") === String(multiTestId)
      ),
  );
  useEffect(() => {
    if (blocker.state === "blocked") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUnsavedWarningOpen(true);
    }
  }, [blocker.state]);

  const handleConfirmLeave = () => {
    setUnsavedWarningOpen(false);
    if (pendingBack.current) {
      pendingBack.current = false;
      onBack(comparison?.multi_model_test_id ?? null, comparison?.candidate_model);
    } else if (blocker.state === "blocked") {
      blocker.proceed();
    }
  };

  const handleCancelLeave = () => {
    setUnsavedWarningOpen(false);
    pendingBack.current = false;
    if (blocker.state === "blocked") blocker.reset();
  };

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const handleBack = () => {
    if (!dirty) {
      onBack(comparison?.multi_model_test_id ?? null, comparison?.candidate_model);
      return;
    }
    pendingBack.current = true;
    setUnsavedWarningOpen(true);
  };

  const progress = useMemo(() => (comparison ? comparisonProgress(comparison.fields) : null), [comparison]);

  if (!comparison) {
    // The error state must not impersonate a spinner; the way back lives here too.
    if (isError) {
      return (
        <div className="flex flex-col items-start gap-3">
          <Button text="← All comparisons" appearance="secondary" className="rounded-sm" onClick={() => onBack(null)} />
          <Paragraph displayType="normal" size="medium" className="text-text-secondary">
            Could not load this comparison — it may have been deleted.
          </Paragraph>
        </div>
      );
    }
    return (
      <div className="flex min-h-[60vh] items-center justify-center" aria-busy="true" aria-label="Loading comparison">
        <ArrowPathIcon className="size-8 animate-spin text-text-tertiary" />
      </div>
    );
  }

  const restart = async (promptKey: string, arm: ArmValue) => {
    if (comparison.learner_test_profile_id === null) return;
    const slotKey = `${promptKey}:${arm}`;
    setRestartingSlots((prev) => new Set(prev).add(slotKey));
    try {
      await startPromptTestRun(promptKey, {
        learner_test_profile_id: comparison.learner_test_profile_id,
        model: arm === Arm.Baseline ? comparison.baseline_model : comparison.candidate_model,
        comparison_id: comparison.id,
        arm,
        use_draft: false,
      });
      await queryClient.invalidateQueries({ queryKey: comparisonQueryKey(comparisonId) });
    } catch {
      notifyError("Failed to start the run.");
    } finally {
      setRestartingSlots((prev) => {
        const next = new Set(prev);
        next.delete(slotKey);
        return next;
      });
    }
  };

  const save = async (): Promise<boolean> => {
    setSaving(true);
    try {
      const resp = await updateEvalModelComparison(comparisonId, {
        notes,
        verdict,
        picks: picksForPatch(picks, baseline?.picks ?? draftPicksFromFields(comparison.fields)),
      });
      // The Orval fetcher resolves on 4xx rather than throwing.
      if (resp.status === 200) {
        // The server now holds exactly these drafts (clears included) —
        // clean immediately rather than after the refetch round-trips.
        setBaseline({ picks, notes, verdict });
        notifySuccess("Comparison saved.");
        // Not awaited: the multi-model refetch advances runs server-side and
        // can take seconds; Next must not wait on it.
        void queryClient.invalidateQueries({ queryKey: comparisonQueryKey(comparisonId) });
        // The landing list stays mounted behind this screen — refresh it or Back shows stale rows.
        void queryClient.invalidateQueries({ queryKey: getListEvalModelComparisonsQueryKey() });
        if (multiTestId != null) {
          // Reviewed-state in the queue bar and work-list derives from picks.
          const key = getGetEvalMultiModelTestQueryKey(multiTestId);
          queryClient.setQueryData<EvalMultiModelTestDetail | null>(key, (prev) =>
            prev ? withSavedPicks(prev, comparisonId, picks) : prev,
          );
          void queryClient.invalidateQueries({ queryKey: key });
        }
        return true;
      }
      notifyError((resp.data as { detail?: string })?.detail ?? "Could not save the comparison.");
      return false;
    } catch {
      // A network-level rejection never reaches the status branches.
      notifyError("Could not save the comparison — check your connection.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const preferLabel: Record<ArmValue, WinnerValue> = {
    [Arm.Baseline]: Winner.Baseline,
    [Arm.Candidate]: Winner.Candidate,
  };

  return (
    <div className={`flex flex-col gap-y-4 ${multiTestId != null && queue.length > 0 ? "pb-6" : "pb-28"}`}>
      {celebrate ? <ConfettiBurst onDone={() => setCelebrate(false)} /> : null}
      {multiTestId != null && queue.length > 0 ? (
        // Full-bleed bar pinned under the app header (h-14, z-40): top-14
        // keeps it from sliding beneath the header, -mx-16 undoes the
        // Shell's px-16 so it spans edge to edge like the header does.
        <div
          ref={queueBarRef}
          className="sticky top-14 z-30 -mx-16 -mt-8 flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border-subtle bg-bg-surface px-16 py-2.5 shadow-sm"
        >
          <div className="flex flex-1 items-center">
            <Button text="← Back to results" appearance="secondary" className="rounded-sm" onClick={handleBack} />
          </div>
          <div className="flex shrink-0 items-center gap-x-4">
            <span className="text-sm font-semibold text-text-primary">Review queue</span>
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-28 overflow-hidden rounded-full bg-bg-default">
                <span
                  className="block h-full rounded-full bg-rio-500 transition-all"
                  style={{ width: `${(queue.filter((q) => q.winner).length / queue.length) * 100}%` }}
                />
              </span>
              <span className="whitespace-nowrap text-xs font-medium text-text-secondary">
                {queue.filter((q) => q.winner).length}/{queue.length} reviewed
              </span>
            </span>
          </div>
          <div className="flex flex-1 items-center justify-end gap-3">
            {dirty ? (
              <span className="whitespace-nowrap text-xs font-semibold text-amber-700" role="status">
                Unsaved — Next saves
              </span>
            ) : null}
            {queueIndex >= 0 ? (
              <span className="whitespace-nowrap text-xs text-text-tertiary">
                Item {queueIndex + 1} of {queue.length}
              </span>
            ) : null}
            {queueIndex > 0 ? (
              <Button
                text="← Prev"
                appearance="secondary"
                className="rounded-sm"
                onClick={() => goToQueueItem(queue[queueIndex - 1])}
              />
            ) : null}
            {nextUnreviewed ? (
              <Button
                text="Next unreviewed →"
                appearance="primary"
                className="rounded-sm"
                onClick={() => goToQueueItem(nextUnreviewed)}
              />
            ) : queue.every((q) => q.winner) ? (
              <span className="text-xs font-medium text-green-700">All flagged prompts reviewed 🎉</span>
            ) : (
              // This item is the LAST unreviewed one — nextUnreviewed skips
              // the current item, so offer the finishing save here instead
              // of prematurely celebrating.
              <Button
                text={saving ? "Saving…" : "Save & finish"}
                appearance="primary"
                className="rounded-sm"
                disabled={saving || !dirty}
                onClick={() => void save()}
              />
            )}
          </div>
          {remaining.length > 0 ? (
            // Names what's left so Next's target is never a guess (two items
            // can share one comparison page).
            <div className="flex w-full flex-wrap items-center gap-1.5 text-xs">
              <span className="text-text-tertiary">Left:</span>
              {remaining.slice(0, MAX_REMAINING_SHOWN).map((item) => {
                const current = item.comparison_id === comparisonId && item.prompt_key === focusPromptKey;
                return (
                  <button
                    key={`${item.comparison_id}:${item.prompt_key}`}
                    type="button"
                    aria-current={current ? "true" : undefined}
                    onClick={() => void goToQueueItem(item)}
                    className={`rounded-full border px-2 py-0.5 ${
                      current
                        ? "border-border-medium bg-bg-default font-semibold text-text-primary"
                        : "border-border-subtle text-text-secondary hover:bg-bg-default"
                    }`}
                  >
                    {item.field_title || item.prompt_key} · {item.template_name}
                  </button>
                );
              })}
              {remaining.length > MAX_REMAINING_SHOWN ? (
                <span className="text-text-tertiary">+{remaining.length - MAX_REMAINING_SHOWN} more</span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="flex items-start justify-between gap-x-6">
        <div>
          {/* In queue mode the sticky bar carries Back — no duplicate here. */}
          {multiTestId == null || queue.length === 0 ? (
            <Button
              text={multiTestId != null ? "← Back to results" : "← All comparisons"}
              appearance="secondary"
              className="rounded-sm"
              onClick={handleBack}
            />
          ) : null}
          <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="text-lg font-semibold text-text-primary">{comparison.template_name}</h2>
            <span className="flex items-center gap-1.5 text-sm text-text-secondary" title="current → candidate">
              <ModelBadge model={comparison.baseline_model} />
              <span className="text-text-tertiary">→</span>
              <ModelBadge model={comparison.candidate_model} />
            </span>
          </div>
          <ScoreTotal
            summary={comparison.score_summary}
            fields={comparison.fields}
            showJumpLinks={multiTestId == null}
          />
        </div>
        {progress ? (
          <span className="shrink-0 text-xs text-text-tertiary">
            {progress.complete} of {progress.total} runs complete
            {progress.failed > 0 ? `, ${progress.failed} failed` : ""}
            {progress.pending > 0 ? `, ${progress.pending} not started` : ""}
          </span>
        ) : null}
      </div>

      {comparison.fields.map((field) => {
        const pick = picks[field.prompt_key];
        const match = outputsCompared(field);
        return (
          <section
            key={field.prompt_key}
            id={fieldAnchor(field.prompt_key)}
            className="scroll-mt-20 rounded-lg border border-border-subtle bg-bg-surface p-4"
          >
            <div className="flex items-baseline gap-x-2">
              <span className="text-sm font-medium text-text-primary">{field.field_title || field.prompt_key}</span>
              <code className="rounded bg-bg-default px-1 text-xs text-text-tertiary">{field.prompt_key}</code>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {(
                [
                  [Arm.Baseline, field.baseline, comparison.baseline_model, "Current"],
                  [Arm.Candidate, field.candidate, comparison.candidate_model, "Candidate"],
                ] as const
              ).map(([arm, run, model, heading]) => (
                <div
                  key={arm}
                  className={`flex flex-col rounded-md border bg-bg-default p-3 ${
                    runIsDisqualified(run) ? "border-red-300" : "border-border-subtle"
                  }`}
                >
                  <div className="mb-2 flex items-center justify-between gap-x-2">
                    <Label size="small" colorType="primary" className="font-semibold uppercase tracking-wide">
                      {heading}
                    </Label>
                    <ModelBadge model={run?.model || model} />
                  </div>
                  {arm === Arm.Candidate ? (
                    <div>
                      {field.pairwise_verdict ? (
                        <span className="mb-2 mr-2 inline-block">
                          <PairwiseVerdictChip verdict={field.pairwise_verdict} summary={field.pairwise_summary} />
                        </span>
                      ) : null}
                      <JudgeStateChip field={field} />
                      {field.pairwise_verdict ? (
                        <PairwiseDifferences
                          verdict={field.pairwise_verdict}
                          summary={field.pairwise_summary}
                          differences={field.pairwise_differences}
                        />
                      ) : null}
                    </div>
                  ) : null}
                  {armState(run) === ArmState.Complete ? (
                    <ChecksSummary
                      checks={run!.check_results ?? []}
                      onHighlight={(quotes) =>
                        setHighlights((prev) => ({
                          ...prev,
                          [highlightKey(comparisonId, field.prompt_key, arm)]: quotes,
                        }))
                      }
                    />
                  ) : null}
                  <div className="flex-1">
                    <ArmCell
                      promptKey={field.prompt_key}
                      run={run}
                      onRestart={
                        comparison.learner_test_profile_id === null ? null : () => void restart(field.prompt_key, arm)
                      }
                      restarting={restartingSlots.has(`${field.prompt_key}:${arm}`)}
                      diffAgainst={
                        arm === Arm.Candidate && match === OutputMatch.Different ? field.baseline!.result : null
                      }
                      highlights={highlights[highlightKey(comparisonId, field.prompt_key, arm)] ?? NO_QUOTES}
                      onClearHighlights={() =>
                        setHighlights((prev) => ({
                          ...prev,
                          [highlightKey(comparisonId, field.prompt_key, arm)]: NO_QUOTES,
                        }))
                      }
                    />
                  </div>
                  {/* The preference lives under the output it refers to. */}
                  <button
                    type="button"
                    className={`mt-3 w-full ${pickClasses(pick?.winner === preferLabel[arm])}`}
                    onClick={() => setPicks((prev) => togglePick(prev, field.prompt_key, preferLabel[arm]))}
                  >
                    {pick?.winner === preferLabel[arm] ? "✓ Preferred" : "Prefer this one"}
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-3 flex items-center justify-center gap-3">
              <button
                type="button"
                className={pickClasses(pick?.winner === Winner.Tie)}
                onClick={() => setPicks((prev) => togglePick(prev, field.prompt_key, Winner.Tie))}
              >
                {pick?.winner === Winner.Tie ? "✓ Tie" : "It’s a tie"}
              </button>
            </div>
            {/* Own row so it reads as part of any pick, not just a tie. */}
            {pick ? (
              <div className="mt-2 flex justify-center">
                <input
                  type="text"
                  value={pick.note}
                  onChange={(e) => setPicks((prev) => setPickNote(prev, field.prompt_key, e.target.value))}
                  placeholder="Why did you pick this? (optional)"
                  aria-label={`Note for ${field.field_title || field.prompt_key}`}
                  className="w-96 max-w-full rounded border border-border-subtle bg-bg-default px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-limestone-900"
                />
              </div>
            ) : null}
          </section>
        );
      })}

      <Dialog
        title="Leave without saving?"
        open={unsavedWarningOpen}
        // ESC/backdrop/X must mean "Stay" or the router blocker stays armed.
        setOpen={(open: boolean) => {
          if (!open) handleCancelLeave();
          else setUnsavedWarningOpen(true);
        }}
        primaryButton={{ text: "Leave without saving", onClick: handleConfirmLeave }}
        secondaryButton={{ text: "Stay", onClick: handleCancelLeave }}
        size="sm"
      >
        <p className="text-sm text-text-secondary">
          You have unsaved feedback on this comparison — picks, notes, or the verdict. If you leave now, it will be
          lost.
        </p>
      </Dialog>

      {/* Every flagged prompt reviewed → don't make the reviewer scroll to
          the bottom section to remember the verdict; hand it to them. */}
      <Dialog
        title="Verdict for this comparison"
        open={verdictDialogOpen}
        setOpen={setVerdictDialogOpen}
        primaryButton={{
          text: saving ? "Saving…" : "Save verdict",
          onClick: () => {
            void save().then((ok) => {
              if (ok) setVerdictDialogOpen(false);
            });
          },
        }}
        secondaryButton={{ text: "Later", onClick: () => setVerdictDialogOpen(false) }}
        size="sm"
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-text-secondary">
            All flagged prompts are reviewed 🎉 — record your overall call for{" "}
            <span className="font-medium text-text-primary">{modelLabel(comparison.candidate_model)}</span>.
          </p>
          <div className="flex flex-wrap items-center gap-1">
            {(Object.values(Verdict) as VerdictValue[]).map((value) => (
              <button
                key={value}
                type="button"
                className={pickClasses(verdict === value)}
                onClick={() => setVerdict(value)}
              >
                {VERDICT_LABELS[value]}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Overall notes — what should the person flipping the default know?"
            className="w-full rounded border border-border-subtle bg-bg-default px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-limestone-900"
          />
        </div>
      </Dialog>

      {/* Notes + verdict. In queue mode it's a titled page section (not a
          bar — the sticky review bar owns navigation/saving, and edits
          here ride along with Next / Save & finish). Standalone
          comparisons keep the fixed footer as their only save surface. */}
      {multiTestId != null && queue.length > 0 ? (
        <section className="rounded-lg border border-border-subtle bg-bg-surface p-4">
          <h3 className="text-sm font-normal uppercase tracking-wide text-text-primary">Verdict for this comparison</h3>
          <p className="mt-0.5 text-xs text-text-tertiary">
            Saved automatically with Next / Save &amp; finish — or right here.
          </p>
          <div className="mt-3">
            <FeedbackControls
              notes={notes}
              onNotes={setNotes}
              dirty={dirty}
              verdict={verdict}
              onVerdict={setVerdict}
              saving={saving}
              onSave={() => void save()}
            />
          </div>
        </section>
      ) : (
        <div className="fixed inset-x-0 bottom-0 border-t border-border-subtle bg-bg-surface px-16 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <FeedbackControls
            notes={notes}
            onNotes={setNotes}
            dirty={dirty}
            verdict={verdict}
            onVerdict={setVerdict}
            saving={saving}
            onSave={() => void save()}
          />
        </div>
      )}
    </div>
  );
};
